// samplingEventConstants.js
// This file contains all the constants for the sampling event form and tables

// Define view constants for better readability
export const SAMPLING_EVENT_VIEWS = {
  TABLE: "TABLE",
  CREATE_FORM: "CREATE_FORM",
  EVENT_FORM: "EVENT_FORM",
  WELL_UPLOADS: "WELL_UPLOADS",
};

// Defining inspection option constants for each drop down in the inspection tab
export const INSPECTION_OPTIONS = [
  { value: "Y", label: "Yes (Y)" },
  { value: "N", label: "No (N)" },
  { value: "NA", label: "Not Applicable (NA)" },
  { value: "NN", label: "Not Necessary (NN)" },
  { value: "AG", label: "Above Ground (AG)" },
  { value: "FS", label: "Flush with Surface (FS)" },
  { value: "ND", label: "Not Determined (ND)" },
];

// Defining bottom conditions for dropdown
export const BOTTOM_CONDITIONS_OPTIONS = [
  { value: "1- Hard", label: "1- Hard" },
  { value: "2-Soft", label: "2-Soft" },
  { value: "3-Undetermined", label: "3-Undetermined" },
  { value: "N/A", label: "N/A" },
];

// List of fields that we need to validate for the inspection section of the sampling events, may not be the best place for this list, but this list shouldn't change very much and if it does we will know where to find it-
// Has the field and the message to convey if the field is not filled out
export const INSPECTION_FIELDS_TO_VALIDATE = [
  { field: "facilityMapEasyToFind", label: "Well on facility map" },
  { field: "aboveGroundFlushMount", label: "Flush mount or above ground" },
  { field: "freeOfDamage", label: "Free of physical damage" },
  { field: "legibleWellId", label: "Legible well ID" },
  { field: "casingProtected", label: "Casing protected" },
  { field: "casingSecure", label: "Casing secure" },
  { field: "surfacePadCracked", label: "Surface pad/seal eroded" },
  {
    field: "surfacePadProneWaterPonding",
    label: "Surface pad prone to water ponding",
  },
  { field: "weedsTrash", label: "Free of weeds and trash" },
  { field: "wellSecureLocked", label: "Well secure" },
  { field: "surveyGaugingMarks", label: "Survey/gauging marks" },
  { field: "bottomConditions", label: "Bottom conditions" },
  { field: "measuredTotalDepth", label: "Measured total depth" },
  { field: "measuredDepthToWater", label: "Measured depth to water" },
  {
    field: "measuredTotalDepthNm",
    label: "Total depth not measured",
  },
  {
    field: "measuredDepthToWaterNm",
    label: "Depth to water not measured",
  },
  {
    field: "inspectionCompletedDate",
    label: "Inspection completed date not selected",
  },
];

// Defining the purge device and sampling device for our sections
export const PURGE_DEVICE_OPTIONS = [
  { value: "Impeller Pump", label: "Impeller Pump" },
  { value: "Bladder Pump", label: "Bladder Pump" },
  { value: "Peristaltic Pump", label: "Peristaltic Pump" },
  { value: "Disposable Bailer", label: "Disposable Bailer" },
  { value: "Other", label: "Other" },
];

// Defining the sampling device options for our sections, although these are basically the same as above, creating a new array in case it needs to be updated without any serious issues
export const SAMPLING_DEVICE_OPTIONS = [
  { value: "Impeller Pump", label: "Impeller Pump" },
  { value: "Bladder Pump", label: "Bladder Pump" },
  { value: "Peristaltic Pump", label: "Peristaltic Pump" },
  { value: "Disposable Bailer", label: "Disposable Bailer" },
  { value: "Other", label: "Other" },
];

// Defining the sample odor options for our sections, this can be updated to include the odor types from the sample if needed.
export const SAMPLE_ODOR_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

// An Array of error label to display if validation checks find error issues. The keys need to match up with the properties of the Wells data class we are using in the Sampling Events Form. Contains the label for the error, conditional means if the field is conditional based on another field being checked or having input of some kind.
export const SAMPLING_FIELD_ERROR_LABELS = {
  wellSampleId: { label: "Sample ID", conditional: false },
  weatherConditions: { label: "Weather Conditions", conditional: false },
  samplingStartDate: { label: "Sampling Start Date", conditional: false },
  samplingStartTime: { label: "Sampling Start Time", conditional: false },
  samplingEndDate: { label: "Sampling End Date", conditional: false },
  samplingEndTime: { label: "Sampling End Time", conditional: false },
  sampleTargetDepth: { label: "Target Sample Depth", conditional: true },
  extractionRate: { label: "Extraction Rate", conditional: true },
  purgeDevice: { label: "Purge Device", conditional: true },
  samplingDevice: { label: "Sampling Device", conditional: false },
  measuredDepthToWaterSampling: {
    label: "Depth to GW BTOC (ft)",
    conditional: false,
  },
  measuredTotalDepthSampling: {
    label: "Measure TD BTOC (ft)",
    conditional: false,
  },
  samplingBottomConditions: { label: "Bottom Condition", conditional: false },
  lnaplThickness: { label: "LNAPL Thickness", conditional: true },
  lnaplOdor: { label: "LNAPL Odor", conditional: true },
  lnaplColor: { label: "LNAPL Color", conditional: true },
  lnaplOther: { label: "LNAPL Other", conditional: true },
  dnaplThickness: { label: "DNAPL Thickness", conditional: true },
  dnaplOdor: { label: "DNAPL Odor", conditional: true },
  dnaplColor: { label: "DNAPL Color", conditional: true },
  dnaplOther: { label: "DNAPL Other", conditional: true },
  sampleTime: { label: "Sampling Time", conditional: false },
  qcSamples: { label: "QC Samples", conditional: false },
  sampleAppearance: { label: "Sample Appearance", conditional: false },
  sampleColor: { label: "Sample Color", conditional: false },
  sampleOdor: { label: "Sample Odor", conditional: false },
  purgeVolumeIfPurgeThreeWvs: {
    label: "Purge Volume (3 WVs)",
    conditional: true,
  },
  unableToCollectSample: {
    label: "Unable to Collect Sample",
    conditional: true,
  },
  unableToCollectSampleReason: {
    label: "Unable to Collect Sample Reason",
    conditional: true,
  },
};

export const FIELD_MEASUREMENT_ERROR_LABELS = {
  measurementTime: { label: "Measurement Time", conditional: false },
  extractedVolume: { label: "Extracted Volume", conditional: false },
  dtw: { label: "DTW", conditional: false },
  temperature: { label: "Temperature", conditional: false },
  ph: { label: "PH", conditional: false },
  specificConductance: { label: "Specific Conductance", conditional: false },
  orp: { label: "ORP", conditional: false },
  doMeasurement: { label: "DO Measurement", conditional: false },
  turbidity: { label: "Turbidity", conditional: false },
};

export const GALLONS_PER_FOOT_CONVERSION_LIST = {
  1.25: 0.08,
  2: 0.17,
  3: 0.38,
  4: 0.66,
  6: 1.5,
  8: 2.6,
  12: 5.81,
};

export const DEFAULT_GALLONS_PER_FOOT = 0.0408;

// Field Measurement specifc unit options

// Extract Volume Unit Options
export const EXTRACTED_VOLUME_UNIT_OPTIONS = [
  { value: "gal", label: "gal" },
  { value: "ml", label: "ml" },
];

// Specific Conductance Unit Options
export const SPECIFIC_CONDUCTANCE_UNIT_OPTIONS = [
  { value: "ms/cm", label: "ms/cm" },
  { value: "us/cm", label: "us/cm" },
];
